<script setup lang="ts">
import {type ObjectId, type Tag, TagNamespace, translate} from "~/composables/charon";

const { charon } = useCharon()
const { t, locale } = useI18n()

const props = defineProps<{
  tagIds: ObjectId[]
  expectedLocales: string[]
  namespace: TagNamespace
}>()

const emit = defineEmits<{
  (e: "update:tagIds", value: ObjectId[]): void
}>()

const tagIds = computed({
  get: () => props.tagIds,
  set: (val) => emit('update:tagIds', val)
})

const query = ref("")
const creatorOpen = ref(false)

function select(id: ObjectId | null) {
  if (id == null) {
    creatorOpen.value = true
    return
  }
  tagIds.value = [...tagIds.value, id]
  query.value = ""
}

function remove(tagId: ObjectId) {
  tagIds.value = tagIds.value.filter(id => id !== tagId)
}

const { data: tags } = useAsyncData(
  () => charon<Tag[]>(`/tags`, {
    params: {
      "namespace[eq]": props.namespace,
      [`locales.${locale.value}.label[fuzzyi]`]: query.value,
      page: 0,
      size: 10,
    },
  }),
  {
    lazy: true,
    watch: [() => props.namespace, query, locale],
  }
)

const items = computed(() => (tags.value ?? [])
  .filter(tag => !tagIds.value.includes(tag.id!!))
  .map(tag => {
    const translated = translate(tag.locales, locale.value)
    return {
      key: tag.id!!,
      label: translated?.label ?? "-",
      title: translated?.description ?? "-",
    }
  })
)

function created(id: ObjectId) {
  select(id)
  creatorOpen.value = false
}

// a tag template based on the current query and namespace
const queryTag = computed<Partial<Tag>>(() => ({
  namespace: props.namespace,
  locales: {
    [locale.value]: {
      label: query.value,
      description: "",
    }
  }
}))
</script>

<template>
  <div class="bg-white px-2 flex flex-row gap-4 border focus-within:ring">
    <template v-for="tagId in tagIds" :key="tagId">
      <TagsInputItem
        :tag-id="tagId"
        :expected-locales="props.expectedLocales"
        @deleted="() => remove(tagId)"
        @remove="() => remove(tagId)"
      />
    </template>
    <Combobox
      :items="items"
      :selected="null"
      @update:selected="val => select(val?.key ?? null)"
      v-model:query="query"
      :empty-text="t('jc.component.tagsInput.emptyText')"
      empty-text-selectable
      :placeholder="t('jc.component.tagsInput.placeholder')"
    />
    <TagEditor
      :tag="queryTag"
      :namespace="props.namespace"
      :open="creatorOpen"
      @created="created"
      @cancel="creatorOpen = false"
    />
  </div>
</template>

<style scoped lang="postcss">

</style>
